import { CompanionFace, type CompanionFaceMood } from '@/components/companion/feed/CompanionFace';
import {
  COMPANION_PANEL_ID,
  COMPANION_TRIGGER_ID,
} from '@/components/companion/feed/companion-panel-ids';
import { CompanionTrigger } from '@/components/companion/feed/CompanionTrigger';
import { Tooltip } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/auth-context';
import {
  isCompanionPanelUnavailable,
  useCompanionPanelConversationQuery,
} from '@/hooks/queries/use-companion-panel-conversation-query';
import { useCompanionFeedQuery } from '@/hooks/queries/use-companion-feed-query';
import { Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  formatTriggerCount,
  resolvePanelTriggerState,
  type PanelTriggerState,
} from './companion-panel-trigger-state';
import { useOpenCompanionPanel } from './use-open-companion-panel';

/**
 * The conversational panel's place in the top bar. Always a word beside the
 * face, so the brand never has to guess what the orb is; below `md` the word
 * moves into the accessible name only.
 */

const MOOD_BY_STATE: Record<PanelTriggerState, CompanionFaceMood> = {
  actions: 'alert',
  analysing: 'look',
  idle: 'doze',
};

const isApplePlatform = (): boolean =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);

export const CompanionPanelTrigger = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const brandId = user?.currentBrandId ?? '';
  const { data: feed } = useCompanionFeedQuery(brandId);
  const { isPending, error } = useCompanionPanelConversationQuery(brandId);
  const { isOpen, toggle } = useOpenCompanionPanel();

  // Closed for the organization (the server kill switch contains an incident):
  // the cohort gets the feed's trigger back, never an empty top bar.
  if (isCompanionPanelUnavailable(error)) return <CompanionTrigger />;
  // Neither trigger until the panel has answered once, so the wrong one never flashes.
  if (isPending) return null;

  const { state, count } = resolvePanelTriggerState(feed);
  const label =
    state === 'actions'
      ? t('companion.panelTrigger.actions', { count, label: formatTriggerCount(count) })
      : state === 'analysing'
        ? t('companion.trigger.analysing')
        : t('companion.panelTrigger.idle');
  const shortcut = isApplePlatform() ? '⌘K' : 'Ctrl K';

  return (
    <Tooltip
      ids={{ trigger: COMPANION_TRIGGER_ID }}
      content={t('companion.panelTrigger.tooltip', { shortcut })}
      openDelay={300}
    >
      <Button
        id={COMPANION_TRIGGER_ID}
        data-testid="companion-panel-trigger"
        data-state={state}
        variant="plain"
        h="36px"
        minW="36px"
        pl="6px"
        pr={{ base: '6px', md: '12px' }}
        gap="8px"
        borderRadius="9999px"
        bg="announcement.chipBg"
        color="announcement.indicatorFg"
        _hover={{ bg: 'announcement.controlHoverBg' }}
        aria-expanded={isOpen}
        aria-controls={COMPANION_PANEL_ID}
        aria-keyshortcuts="Meta+K Control+K"
        aria-label={label}
        onClick={toggle}
      >
        <CompanionFace mood={MOOD_BY_STATE[state]} size={24} />
        <Text
          display={{ base: 'none', md: 'inline' }}
          fontFamily="heading"
          fontSize="13px"
          fontWeight={state === 'actions' ? '600' : '500'}
          color={state === 'actions' ? 'announcement.indicatorFg' : 'announcement.indicatorFgMuted'}
          lineHeight="1"
        >
          {label}
        </Text>
      </Button>
    </Tooltip>
  );
};
